import React, { useEffect, useState } from "react"
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, List, ListItem, ListItemText, Skeleton, Typography } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import PageWrapper from "Pages/@PageWrapper"
import { AppCrudTable, AppButtonIcon } from "@components"
import { AUTO_EUROPE_TOOL_ROUTES, BACKEND_APIS } from "@constants/routes"
import { getExcel } from "@ui-seeker/utils"

const SEASON_SETS_API = `${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/season-sets`
import {
	autoEuropeSeasonSetsDrawerData,
	autoEuropeSeasonSetsIsLoading,
	autoEuropeSeasonSetsListData,
} from "Pages/AutoEuropeTool/SeasonSets/@signals"
import AutoEuropeSeasonSetsDrawer from "Pages/AutoEuropeTool/SeasonSets/Drawer"
import useAutoEuropeSeasonSetsTable from "Pages/AutoEuropeTool/SeasonSets/@hooks/useTable"
import useAutoEuropeSeasonSetsFormSubmit from "Pages/AutoEuropeTool/SeasonSets/@hooks/useFormSubmit"
import useAutoEuropeSeasonSetsActions from "Pages/AutoEuropeTool/SeasonSets/@hooks/useActions"
import useAutoEuropeSeasonSetsEdit from "Pages/AutoEuropeTool/SeasonSets/@hooks/useEdit"
import useAutoEuropeSeasonSetsClone from "Pages/AutoEuropeTool/SeasonSets/@hooks/useClone"

const EMPTY_SEASON_RULES = { dateRanges: [], followingDays: [], nextSaturday: [], finalSeason: false }
const EMPTY_FORM = { name: null, seasonRules: EMPTY_SEASON_RULES }

function rulesSummary(rules) {
	if (!rules) return '-'
	const parts = []
	if (rules.dateRanges?.length) parts.push(`${rules.dateRanges.length} date range${rules.dateRanges.length > 1 ? 's' : ''}`)
	if (rules.followingDays?.length) parts.push(rules.followingDays.map(d => `${d.from}–${d.to}d`).join(', '))
	if (Array.isArray(rules.nextSaturday) && rules.nextSaturday.length) parts.push(`${rules.nextSaturday.length} sat`)
	if (rules.finalSeason) parts.push('Final season')
	return parts.length ? parts.join(' · ') : '-'
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

function ReportsDialog({ seasonSetId, seasonSetName, onClose }) {
	const [tools, setTools] = useState(null)

	useEffect(() => {
		fetch(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/list`)
			.then(r => r.json())
			.then(data => {
				const matching = (data || []).filter(t => t.seasonSetId === seasonSetId)
				setTools(matching)
			})
			.catch(() => setTools([]))
	}, [seasonSetId])

	const hasReports = tools?.some(t => t.reportsName?.length)

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ pr: 6 }}>
				Reports using <strong>{seasonSetName}</strong>
				<IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
					<IconSeeker icon="xmark"/>
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{tools === null
					? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress/></Box>
					: !hasReports
						? <Typography color="text.secondary" sx={{ py: 1 }}>No reports assigned to this season set.</Typography>
						: <List dense disablePadding>
							{tools.map(tool => (
								tool.reportsName?.length ? (
									<React.Fragment key={tool.id}>
										<ListItem disablePadding sx={{ pt: 1 }}>
											<ListItemText
												primary={<Typography variant="subtitle2">{tool.name}</Typography>}
												secondary={tool.reportsName.join(', ')}
											/>
										</ListItem>
										<Divider/>
									</React.Fragment>
								) : null
							))}
						</List>
				}
			</DialogContent>
		</Dialog>
	)
}

function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useAutoEuropeSeasonSetsEdit(row.id)
	const { onClone, isLoading: isCloneLoading } = useAutoEuropeSeasonSetsClone(row.id)
	const [showReports, setShowReports] = useState(false)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			{isCloneLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="clone" tooltip="Clone" onClick={e => { e.stopPropagation(); onClone() }}/>
			}
			<AppButtonIcon
				icon="file-lines"
				tooltip="Reports using this season set"
				onClick={e => { e.stopPropagation(); setShowReports(true) }}
			/>
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.id, row.name) }}
			/>
			{showReports && (
				<ReportsDialog
					seasonSetId={row.id}
					seasonSetName={row.name}
					onClose={() => setShowReports(false)}
				/>
			)}
		</Box>
	)
}

export default function AutoEuropeSeasonSetsPage() {
	const { getTableData } = useAutoEuropeSeasonSetsTable()
	const { onSubmit } = useAutoEuropeSeasonSetsFormSubmit({ getTableData })
	const { onDelete } = useAutoEuropeSeasonSetsActions()

	useEffect(() => {
		getTableData()
	}, [])

	return <PageWrapper
		head={`PS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.SEASON_SETS_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.SEASON_SETS_SUBTITLE}
	>
		<AutoEuropeSeasonSetsDrawer onSubmit={onSubmit}/>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeSeasonSetsIsLoading.value
					? <Skeleton variant="rectangular" height={400}/>
					: <AppCrudTable
						title="Season Sets"
						data={autoEuropeSeasonSetsListData.value}
						columns={[
							{
								name: 'name',
								label: 'Name',
								options: {
									filterType: 'textField',
									customBodyRowRender: v => centered(v),
								}
							},
							{
								name: 'seasonRules',
								label: 'Rules',
								options: {
									filter: false,
									sort: false,
									customBodyRowRender: v => centered(rulesSummary(v)),
								}
							},
							{
								name: 'id',
								label: 'Actions',
								options: {
									filter: false,
									sort: false,
									customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete}/>,
								}
							},
						]}
						options={{
							customToolbar: () => (
								<Box sx={{ ml: 4 }}>
									<ButtonLoadingSeeker
										startIcon={<IconSeeker icon="file-plus"/>}
										onClick={() => { autoEuropeSeasonSetsDrawerData.value = { ...EMPTY_FORM } }}
									>
										Create
									</ButtonLoadingSeeker>
								</Box>
							),
							onDownload: () => getExcel(`${SEASON_SETS_API}/list/excel`, {}),
						}}
						rowActions={row => [
							{ label: 'Delete', icon: 'trash', onClick: () => onDelete(row.id, row.name) },
						]}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}
